import { logger } from "@artis/logger";

/**
 * Handles the "Failed to fetch dynamically imported module" error.
 *
 * Our app is code-split: routes and components like Settings are loaded via
 * `lazy(() => import(...))` / `lazyRouteComponent(() => import(...))`. Each build
 * produces hashed chunk filenames (e.g. `Settings-98nmA1QH.js`). When a new
 * version is deployed, the old hashed files are removed from the server.
 *
 * A user who loaded the app *before* a deploy still references the old chunk
 * names in memory. The moment they trigger a lazy import (e.g. open Settings),
 * the browser requests a chunk that no longer exists and the import rejects with
 * "Failed to fetch dynamically imported module". Vite surfaces this via the
 * `vite:preloadError` window event.
 *
 * The only reliable recovery is to reload the page so the browser fetches a
 * fresh `index.html` that points at the current chunk filenames. We guard
 * against reload loops in case the failure is caused by something other than a
 * stale deploy (e.g. the chunk is genuinely missing/broken or the user is
 * offline), so we never trap the user in an endless refresh.
 */

const RELOAD_TIMESTAMP_KEY = "artis:chunk-reload-at";
// If we already reloaded within this window, assume the reload did not fix the
// problem and stop reloading to avoid an infinite loop.
const RELOAD_LOOP_WINDOW_MS = 30_000;

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "unable to preload css",
];

function isDynamicImportError(reason: unknown): boolean {
  const message =
    reason instanceof Error ? reason.message : typeof reason === "string" ? reason : String((reason as Error)?.message);
  if (!message) return false;
  const normalized = message.toLowerCase();
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern));
}

function reloadOncePerWindow(context: string): void {
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY)) || 0;
  } catch {
    // sessionStorage can be unavailable (e.g. privacy mode); fall through and
    // attempt the reload anyway since it's the user's best chance of recovery.
  }

  const now = Date.now();
  if (lastReloadAt && now - lastReloadAt < RELOAD_LOOP_WINDOW_MS) {
    logger.error("Dynamic import failed again shortly after reloading; not reloading to avoid a loop", {
      context,
      tags: ["chunk-reload"],
    });
    return;
  }

  try {
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(now));
  } catch {
    // Ignore storage failures; we still want to reload.
  }

  logger.warn("Stale chunk detected, reloading page to fetch the latest deploy", {
    context,
    tags: ["chunk-reload"],
  });
  window.location.reload();
}

/**
 * Registers global listeners that recover from stale code-split chunks after a
 * deploy by reloading the page. Safe to call once at app startup.
 */
export function installChunkReloadHandler(): void {
  // Vite's official hook for failed dynamic imports.
  window.addEventListener("vite:preloadError", (event) => {
    // Prevent Vite from throwing the error to the console/error boundary since
    // we're recovering by reloading.
    event.preventDefault();
    reloadOncePerWindow("vite:preloadError");
  });

  // Fallback for dynamic import rejections that don't surface as a
  // vite:preloadError (e.g. the import() promise rejecting directly).
  window.addEventListener("unhandledrejection", (event) => {
    if (isDynamicImportError(event.reason)) {
      event.preventDefault();
      reloadOncePerWindow("unhandledrejection");
    }
  });
}
